// AvatarWardrobe — Grid display of user's avatar collection
import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF } from '@react-three/drei';
import { Loader, Check, Tag, Trash2, Plus, ShoppingBag, Gift, Shirt, Wand2, Upload, Pencil } from 'lucide-react';
import * as THREE from 'three';
import {
  Avatar,
  fetchMyAvatars,
  setActiveAvatar,
  deleteAvatar,
  updateAvatar,
  getCredits,
  migrateLocalStorageAvatar,
} from '../services/avatarService';
import AvatarLooks, { LOOK_THUMB_MARK } from './AvatarLooks';
import AvatarOutfitStudio from './AvatarOutfitStudio';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { useLookTexture } from '../utils/useLookTexture';
import { useWorldStore } from '../stores/worldStore';
import { captureModelThumbnail } from '../utils/captureModelThumbnail';
import { uploadBytesToStorage } from '../utils/uploadToStorage';
import { finishAvatarBuild } from '../utils/buildAvatarFromImage';
import { supabase } from '../config/supabase';
import { vrmBlocksTransfer } from '../avatar/vrm/vrmFormat';
import { isBuiltAvatar, loadRecipe } from '../avatar/vrm/builder/recipe';
import type { BuilderSelection } from '../avatar/vrm/builder/catalog';
import './AvatarWardrobe.css';

// VRM upload (validation pulls in the VRM loader) loads on demand.
const VrmUploadDialog = React.lazy(() => import('./vrm/VrmUploadDialog'));
// Avatar Builder (beta): parts + AI picker, saves a VRM like an upload.
const VrmBuilderDialog = React.lazy(() => import('./vrm/VrmBuilderDialog'));
const isVrmRow = (a: Avatar) => a.creation_params?.source === 'vrm';

// A thumbnail_url is unreliable if it's missing or points at Meshy's CDN (those
// links expire / return malformed URLs). Such avatars get a real thumbnail
// rendered from their own model, once.
/** Card picture: the worn look's render, else the avatar's own thumbnail. */
function cardImage(a: Avatar): string | null {
  if (a.active_texture_url) return a.active_look?.thumbnail_url?.includes(LOOK_THUMB_MARK) ? a.active_look.thumbnail_url : null;
  return a.thumbnail_url || null;
}

function needsThumbnail(a: Avatar): boolean {
  return !!a.model_url && (!a.thumbnail_url || a.thumbnail_url.includes('meshy.ai'));
}

interface AvatarWardrobeProps {
  onCreateNew: () => void;
  onAvatarEquipped: (avatar: Avatar) => void;
  /** Open the free Avatar Builder on arrival (/avatars?build=1). */
  openBuilder?: boolean;
}

// Mini 3D preview for avatar cards
const AvatarMiniPreview: React.FC<{ url: string; lookTextureUrl?: string | null }> = ({ url, lookTextureUrl = null }) => {
  const { scene } = useGLTF(url);


  const wrapper = React.useMemo(() => {
    let minY = Infinity;
    const instance = cloneSkeleton(scene);
    instance.traverse((child: any) => {
      if ((child.isMesh || child.isSkinnedMesh) && child.geometry?.attributes?.position) {
        const pos = child.geometry.attributes.position;
        for (let i = 0; i < pos.count; i++) {
          const y = pos.getY(i);
          if (y < minY) minY = y;
        }
      }
    });
    const group = new THREE.Group();
    group.add(instance);
    if (isFinite(minY) && Math.abs(minY) > 0.05) group.position.y = -minY;
    return group;
  }, [scene]);

  useLookTexture(wrapper, lookTextureUrl);
  return <primitive object={wrapper} scale={[1, 1, 1]} />;
};

const AvatarWardrobe: React.FC<AvatarWardrobeProps> = ({ onCreateNew, onAvatarEquipped, openBuilder = false }) => {
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [listModal, setListModal] = useState<Avatar | null>(null);
  const [listPrice, setListPrice] = useState(10);
  const [vrmOpen, setVrmOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(openBuilder);
  // Built avatar reopened in the builder (its recipe; null selection = not found, start fresh).
  const [builderEdit, setBuilderEdit] = useState<{ avatar: Avatar; selection: BuilderSelection | null } | null>(null);
  // Avatar ids whose thumbnail_url failed to load (e.g. dead Meshy CDN links) —
  // fall back to the live 3D model preview instead of a broken image.
  const [thumbErrors, setThumbErrors] = useState<Set<string>>(new Set());
  const backfilledRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    loadData();
    void resumeBuilds();
  }, []);

  // Avatar builds still running server-side (a closed tab, a phone that slept):
  // keep polling them here so the paid avatar lands in the wardrobe.
  const [building, setBuilding] = useState<{ id: string; name: string }[]>([]);
  const resumeBuilds = async () => {
    const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    const { data } = await supabase.from('avatar_builds').select('id, name, status')
      .in('status', ['generating', 'rig_starting', 'rigging', 'saving']).gt('created_at', since);
    if (!data?.length) return;
    setBuilding(data.map((b: any) => ({ id: b.id, name: b.name })));
    for (const b of data as { id: string }[]) {
      finishAvatarBuild(b.id)
        .then((a) => setAvatars((avs) => (avs.some((x) => x.id === a.id) ? avs : [a, ...avs])))
        .catch((e) => console.warn('[Wardrobe] build did not finish', b.id, e?.message))
        .finally(() => setBuilding((list) => list.filter((x) => x.id !== b.id)));
    }
  };

  // Self-heal: for avatars with a missing/broken (Meshy) thumbnail, render a real
  // one from their own model, upload it, and persist it — once per avatar. Run
  // over the freshly-fetched list (not a [avatars] effect) so the per-avatar
  // setAvatars updates below don't re-enter and skip siblings.
  const backfillThumbnails = async (list: Avatar[]) => {
    for (const a of list) {
      if (!needsThumbnail(a) || backfilledRef.current.has(a.id)) continue;
      backfilledRef.current.add(a.id);
      try {
        const blob = await captureModelThumbnail(a.model_url);
        if (!blob) continue;
        const url = await uploadBytesToStorage(blob, `avatars/thumbnails/${a.id}-${Date.now()}.png`, 'image/png');
        await updateAvatar(a.id, { thumbnail_url: url });
        setAvatars((prev) => prev.map((x) => (x.id === a.id ? { ...x, thumbnail_url: url } : x)));
        setThumbErrors((prev) => { const n = new Set(prev); n.delete(a.id); return n; });
        console.log('[Wardrobe] Backfilled thumbnail for avatar', a.id);
      } catch (e) {
        console.warn('[Wardrobe] Thumbnail backfill failed for', a.id, e);
      }
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Migrate localStorage avatar if this is a first-time user
      await migrateLocalStorageAvatar();

      const [avs, creditResult] = await Promise.all([fetchMyAvatars(), getCredits().then(value => ({ value })).catch(() => ({ value: null }))]);
      setAvatars(avs);
      setCredits(creditResult.value);
      void backfillThumbnails(avs); // self-heal broken/missing thumbnails once
    } catch (err) {
      console.error('[Wardrobe] Failed to load:', err);
    }
    setLoading(false);
  };

  const handleEquip = async (avatar: Avatar) => {
    if (avatar.is_active) return;
    setActionLoading(avatar.id);
    try {
      await setActiveAvatar(avatar.id);
      setAvatars(avs => avs.map(a => ({ ...a, is_active: a.id === avatar.id })));
      // Carry the worn look across with the avatar. Without this, activating an
      // avatar drops back to its original texture and the look silently
      // disappears until the panel is touched again.
      useWorldStore.getState().setAvatarLookTextureUrl(
        (avatar as any).active_texture_url || null,
      );
      onAvatarEquipped(avatar);
    } catch (err) {
      console.error('[Wardrobe] Equip failed:', err);
    }
    setActionLoading(null);
  };

  // Which card has its Looks panel open. Only one at a time — several open
  // panels would each poll Meshy independently.
  const [looksFor, setLooksFor] = useState<string | null>(null);
  // A look's picture finished rendering: the card wearing it switches to it.
  const onLookThumbnail = React.useCallback((lookId: string, url: string) => {
    setAvatars((avs) => avs.map((a) => ((a as any).active_look_id === lookId ? { ...a, active_look: { thumbnail_url: url } } : a)));
  }, []);
  // Outfit Studio target. Distinct from Looks: Looks recolours the texture of
  // this avatar; the studio builds a sibling with new garment geometry.
  const [outfitFor, setOutfitFor] = useState<Avatar | null>(null);

  const handleDelete = async (avatar: Avatar) => {
    // The worn avatar can be deleted too: switch to the newest other avatar
    // first (or back to the default avatar when it's the only one).
    const next = avatar.is_active
      ? avatars.filter((a) => a.id !== avatar.id)
          .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))[0]
      : undefined;
    const msg = !avatar.is_active
      ? `Delete "${avatar.name}"? This cannot be undone.`
      : next
        ? `Delete "${avatar.name}"? You're wearing it, so you'll switch to "${next.name}". This cannot be undone.`
        : `Delete "${avatar.name}"? It's your only avatar, so you'll go back to the default avatar. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setActionLoading(avatar.id);
    try {
      if (next) await handleEquip(next);
      await deleteAvatar(avatar.id);
      setAvatars(avs => avs.filter(a => a.id !== avatar.id));
      if (avatar.is_active && !next) {
        useWorldStore.getState().setAvatarLookTextureUrl(null);
        useWorldStore.getState().setAvatarUrl(null);
      }
    } catch (err: any) {
      console.error('[Wardrobe] Delete failed:', err);
      window.alert(`Couldn't delete "${avatar.name}": ${err?.message || 'unknown error'}`);
    }
    setActionLoading(null);
  };

  const handleEdit = async (avatar: Avatar) => {
    setActionLoading(avatar.id);
    const selection = await loadRecipe(avatar);
    setActionLoading(null);
    setBuilderEdit({ avatar, selection });
    setBuilderOpen(true);
  };

  const handleList = async () => {
    if (!listModal) return;
    setActionLoading(listModal.id);
    try {
      await updateAvatar(listModal.id, { is_listed: true, list_price: listPrice });
      setAvatars(avs => avs.map(a => a.id === listModal.id ? { ...a, is_listed: true, list_price: listPrice } : a));
      setListModal(null);
    } catch (err) {
      console.error('[Wardrobe] List failed:', err);
    }
    setActionLoading(null);
  };

  const handleUnlist = async (avatar: Avatar) => {
    setActionLoading(avatar.id);
    try {
      await updateAvatar(avatar.id, { is_listed: false, list_price: 0 });
      setAvatars(avs => avs.map(a => a.id === avatar.id ? { ...a, is_listed: false, list_price: 0 } : a));
    } catch (err) {
      console.error('[Wardrobe] Unlist failed:', err);
    }
    setActionLoading(null);
  };

  if (loading) {
    return (
      <div className="wardrobe-loading">
        <Loader size={24} className="spinning" />
        <p>Loading wardrobe...</p>
      </div>
    );
  }

  return (
    <div className="avatar-wardrobe">
      <div className="wardrobe-header">
        <h3>Avatar Wardrobe</h3>
        <div className="wardrobe-header-right">
          <span className="credits-badge">{credits === null ? 'Credits unavailable' : `${credits} credits`}</span>
          <button className="wardrobe-create-btn wardrobe-vrm-btn" onClick={() => setBuilderOpen(true)} title="Free: build an anime-style VRM avatar from parts, or let AI pick from a description or selfie">
            <Wand2 size={14} /> Build free <span className="wardrobe-beta">Beta</span>
          </button>
          <button className="wardrobe-create-btn wardrobe-vrm-btn" onClick={() => setVrmOpen(true)} title="Upload your own VRoid / VRM avatar">
            <Upload size={14} /> Upload VRM
          </button>
          <button className="wardrobe-create-btn" onClick={onCreateNew}>
            <Plus size={14} /> New Avatar
          </button>
        </div>
      </div>

      {building.length > 0 && (
        <div className="wardrobe-building" role="status">
          <Loader size={14} className="spinning" />
          {building.length === 1 ? `Building "${building[0].name}"…` : `Building ${building.length} avatars…`} It will appear here when it's ready.
        </div>
      )}

      {avatars.length === 0 ? (
        <div className="wardrobe-empty">
          <p>No avatars yet. Build one free in the Avatar Builder, or create a custom one.</p>
          <button className="wardrobe-create-btn" onClick={onCreateNew}>
            <Plus size={16} /> Create Avatar
          </button>
          <button className="wardrobe-create-btn wardrobe-vrm-btn" onClick={() => setBuilderOpen(true)}>
            <Wand2 size={16} /> Build a free avatar <span className="wardrobe-beta">Beta</span>
          </button>
          <button className="wardrobe-create-btn wardrobe-vrm-btn" onClick={() => setVrmOpen(true)}>
            <Upload size={16} /> Upload a VRM
          </button>
        </div>
      ) : (
        <div className="wardrobe-grid">
          {avatars.map(avatar => (
            <div key={avatar.id} className={`wardrobe-card ${avatar.is_active ? 'active' : ''}`}>
              {/* 3D Preview */}
              <div className="wardrobe-card-preview">
                {/* A picture, not a live 3D view, whenever one exists: each card
                    Canvas is its own WebGL context and browsers drop the oldest
                    past ~16, which blanked freshly re-dressed cards. A worn look
                    shows its own render of the avatar in that outfit. */}
                {cardImage(avatar) && !thumbErrors.has(avatar.id) ? (
                  <img
                    src={cardImage(avatar)!}
                    alt={avatar.name}
                    onError={() => setThumbErrors((prev) => new Set(prev).add(avatar.id))}
                  />
                ) : (
                  <Canvas camera={{ position: [0, 0.9, 3.0], fov: 42 }}>
                    <ambientLight intensity={0.6} />
                    <directionalLight position={[2, 3, 2]} intensity={0.8} />
                    <Suspense fallback={null}>
                      <AvatarMiniPreview url={avatar.model_url} lookTextureUrl={(avatar as any).active_texture_url || null} />
                      <Environment files="/hdri/studio_small_03_1k.hdr" />
                    </Suspense>
                    {/* Look at chest height so the whole avatar is framed (was
                        aimed at the feet → only the bottom half showed). */}
                    <OrbitControls enablePan={false} enableZoom={false} target={[0, 0.9, 0]} minPolarAngle={Math.PI / 3} maxPolarAngle={Math.PI / 1.8} />
                  </Canvas>
                )}
                {avatar.is_active && (
                  <span className="active-badge"><Check size={10} /> Active</span>
                )}
                {isVrmRow(avatar) && (
                  <span className="vrm-badge" title={`VRM ${avatar.creation_params?.vrm_version === '0' ? '0.x' : '1.0'} · ${avatar.creation_params?.vrm_license || 'license unknown'}`}>VRM</span>
                )}
                {avatar.is_listed && (
                  <span className="listed-badge"><Tag size={10} /> {avatar.list_price}cr</span>
                )}
              </div>

              {/* Info */}
              <div className="wardrobe-card-info">
                <span className="wardrobe-card-name">{avatar.name}</span>
                <span className="wardrobe-card-date">
                  {new Date(avatar.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* Actions */}
              <div className="wardrobe-card-actions">
                {actionLoading === avatar.id ? (
                  <Loader size={14} className="spinning" />
                ) : (
                  <>
                    {!avatar.is_active && (
                      <button className="wc-btn wc-equip" onClick={() => handleEquip(avatar)} title="Equip">
                        <Check size={12} /> Equip
                      </button>
                    )}
                    {avatar.is_listed ? (
                      <button className="wc-btn wc-unlist" onClick={() => handleUnlist(avatar)} title="Remove listing">
                        <ShoppingBag size={12} /> Unlist
                      </button>
                    ) : vrmBlocksTransfer(avatar.creation_params).sell ? null : (
                      <button className="wc-btn wc-list" onClick={() => { setListModal(avatar); setListPrice(10); }} title="List for sale">
                        <ShoppingBag size={12} /> Sell
                      </button>
                    )}
                    {isBuiltAvatar(avatar.creation_params) && (
                      <button className="wc-btn wc-looks" onClick={() => handleEdit(avatar)} title="Reopen in the Avatar Builder and change any part">
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                    {/* Recolour / Outfit re-texture or rebuild generated avatars; not for uploaded VRMs. */}
                    {!isVrmRow(avatar) && <>
                    <button
                      className={`wc-btn wc-looks ${looksFor === avatar.id ? 'on' : ''}`}
                      onClick={() => setLooksFor(looksFor === avatar.id ? null : avatar.id)}
                      title="Recolour the clothes this avatar already has (same shapes, new colours)"
                    >
                      <Shirt size={12} /> Recolour
                    </button>
                    <button
                      className="wc-btn wc-looks"
                      onClick={() => setOutfitFor(avatar)}
                      title="Build a new outfit — creates a sibling avatar with real new clothes"
                    >
                      <Wand2 size={12} /> Outfit
                    </button>
                    </>}
                    <button className="wc-btn wc-delete" onClick={() => handleDelete(avatar)} title="Delete" aria-label={`Delete ${avatar.name}`}>
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>

              {looksFor === avatar.id && (
                <AvatarLooks
                  avatarId={avatar.id}
                  modelUrl={avatar.model_url}
                  avatarName={avatar.name}
                  originalThumbnailUrl={avatar.thumbnail_url}
                  onClose={() => setLooksFor(null)}
                  onNewOutfit={() => { setLooksFor(null); setOutfitFor(avatar); }}
                  onLookThumbnail={onLookThumbnail}
                  activeLookId={(avatar as any).active_look_id || null}
                  onApplied={(lookId, textureUrl, thumbnailUrl) => {
                    setAvatars((avs) => avs.map((a) => (
                      a.id === avatar.id
                        ? { ...a, active_look_id: lookId, active_texture_url: textureUrl, active_look: lookId ? { thumbnail_url: thumbnailUrl ?? null } : null } as any
                        : a
                    )));
                    setThumbErrors((prev) => { const n = new Set(prev); n.delete(avatar.id); return n; });
                    // Only repaint the rendered avatar if this is the one being
                    // worn — changing a look on an inactive avatar should not
                    // reskin the one the player is currently in.
                    if (avatar.is_active) {
                      useWorldStore.getState().setAvatarLookTextureUrl(textureUrl);
                    }
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* List for sale modal */}
      {listModal && (
        <div className="wardrobe-modal-overlay" onClick={() => setListModal(null)}>
          <div className="wardrobe-modal" onClick={e => e.stopPropagation()}>
            <h4>List "{listModal.name}" for Sale</h4>
            <label>
              Price (credits):
              <input
                type="number"
                min={1}
                value={listPrice}
                onChange={e => setListPrice(Math.max(1, parseInt(e.target.value) || 1))}
              />
            </label>
            <div className="wardrobe-modal-actions">
              <button className="wc-btn" onClick={() => setListModal(null)}>Cancel</button>
              <button className="wc-btn wc-equip" onClick={handleList}>
                <Tag size={12} /> List for {listPrice} credits
              </button>
            </div>
          </div>
        </div>
      )}
      {vrmOpen && (
        <Suspense fallback={null}>
          <VrmUploadDialog
            onClose={() => setVrmOpen(false)}
            onUploaded={(avatar, equipped) => {
              setAvatars((avs) => [avatar, ...avs.map((a) => (equipped ? { ...a, is_active: false } : a))]);
              if (equipped) {
                useWorldStore.getState().setAvatarLookTextureUrl(null);
                onAvatarEquipped(avatar);
              }
            }}
          />
        </Suspense>
      )}
      {builderOpen && (
        <Suspense fallback={null}>
          <VrmBuilderDialog
            editing={builderEdit}
            onClose={() => { setBuilderOpen(false); setBuilderEdit(null); }}
            onSaved={(avatar, equipped, replaced) => {
              const others = (avs: Avatar[]) => avs.filter((a) => a.id !== avatar.id).map((a) => (equipped ? { ...a, is_active: false } : a));
              setAvatars((avs) => (replaced
                ? avs.map((a) => (a.id === avatar.id ? avatar : equipped ? { ...a, is_active: false } : a))
                : [avatar, ...others(avs)]));
              setThumbErrors((prev) => { const n = new Set(prev); n.delete(avatar.id); return n; });
              if (equipped || avatar.is_active) {
                useWorldStore.getState().setAvatarLookTextureUrl(null);
                onAvatarEquipped(avatar);
              }
            }}
          />
        </Suspense>
      )}
      {outfitFor && (
        <AvatarOutfitStudio
          avatar={outfitFor}
          onClose={() => setOutfitFor(null)}
          onCreated={() => { loadData(); }}
        />
      )}
    </div>
  );
};

export default AvatarWardrobe;
